import { useHydrate, useRefs } from '~/framework'
import { fetchPLP } from '~/utils'
import productCart from './product-card-overlay'
import productForm from './product-form'
import expander from './expander'
import imageFadeIn from './image-fade-in'
import { Component } from '@/types'

const PAGE_SIZE = 12

const component: Component = ref => {
  if (!ref.infiniteLoader) return

  const loadingStates = new WeakMap()

  function callback(entries, observer) {
    entries.forEach(async entry => {
      if (!entry.isIntersecting) return

      const isLoading = loadingStates.get(entry.target)

      if (isLoading) return
      if (entry.target.classList.contains('done')) return

      loadingStates.set(entry.target, true)

      try {
        const searchParams = new URLSearchParams(window.location.search)

        if (searchParams.get('layout') === '3') return

        const target = ref.plp.find(
          element =>
            element.dataset.collection ===
            entry.target.dataset.infiniteLoadHandle
        )

        if (!target) return

        let currentPage = entry.target.dataset.page === undefined
          ? entry.target.dataset.initialPage || '2'
          : parseInt(entry.target.dataset.page) + 1

        // Load content while the loader is still close to viewport (handles footer issue)
        // But limit iterations to prevent breaking horizontal scroll
        let iterations = 0
        const maxIterations = 3

        // Always load at least one page when intersecting, then continue if still close to viewport
        do {
          searchParams.set('page', currentPage.toString())
          searchParams.set('limit', PAGE_SIZE.toString())

          const fetchUrl = entry.target.href + '?' + searchParams.toString()

          const { plp } = await fetchPLP(fetchUrl)

          if (!plp) break

          const items = plp.querySelectorAll('li')
          const newPageHasProducts = items?.length > 0 && items?.[0]?.querySelector('a')

          if (newPageHasProducts) {
            entry.target.dataset.page = currentPage.toString()

            // Clone and append items to ensure they're properly added
            const itemsToAppend = Array.from(items)
            target.append(...itemsToAppend)

            // Hydrate after appending
            useHydrate([productCart, expander, imageFadeIn, productForm]).hydrate(
              useRefs({ root: target })
            )

            const hasNextPage = items[0]?.dataset.hasNextPage !== undefined

            if (!hasNextPage || items.length < PAGE_SIZE) {
              entry.target.classList.add('done')
              observer.unobserve(entry.target)
              break
            }

            currentPage++
            iterations++
          } else {
            entry.target.classList.add('done')
            observer.unobserve(entry.target)
            break
          }

          // Small delay to prevent overwhelming the browser
          await new Promise(resolve => setTimeout(resolve, 50))

          // Extra check: if loader is within 750px of viewport and we haven't hit max iterations, continue loading
        } while (
          (entry.target.getBoundingClientRect().top < window.innerHeight + 750 && iterations < maxIterations) ||
          (iterations === 1 && entry.target.getBoundingClientRect().top < window.innerHeight + 200)
        )
      } catch (error) {
        console.error('Error in infinite loader:', error)
      } finally {
        loadingStates.set(entry.target, false)
      }
    })
  }

  const observer = new IntersectionObserver(callback, { rootMargin: '750px' })

  ref.infiniteLoader.forEach(element => {
    observer.observe(element)
  })

  function resetInfiniteLoaders() {
    ref.infiniteLoader.forEach(element => {
      element.removeAttribute('data-page')
      element.classList.remove('done')
      loadingStates.set(element, false)
      observer.observe(element)
    })
  }

  window.addEventListener('se-event.filter.updated', resetInfiniteLoaders)

  return () => {
    observer.disconnect()
  }
}

export default component
